/**
 * @license Copyright (c) 2014-2023, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import {ClassicEditor} from '@ckeditor/ckeditor5-editor-classic';

import {UploadAdapter} from '@ckeditor/ckeditor5-adapter-ckfinder';
import {Alignment} from '@ckeditor/ckeditor5-alignment';
import {Autoformat} from '@ckeditor/ckeditor5-autoformat';
import {Bold, Code, Italic, Strikethrough, Subscript, Superscript, Underline} from '@ckeditor/ckeditor5-basic-styles';
import {BlockQuote} from '@ckeditor/ckeditor5-block-quote';
import {CodeBlock} from '@ckeditor/ckeditor5-code-block';
import {Essentials} from '@ckeditor/ckeditor5-essentials';
import {FindAndReplace} from '@ckeditor/ckeditor5-find-and-replace';
import {FontBackgroundColor, FontColor, FontFamily, FontSize} from '@ckeditor/ckeditor5-font';
import {Heading} from '@ckeditor/ckeditor5-heading';
import {Highlight} from '@ckeditor/ckeditor5-highlight';
import {HorizontalLine} from '@ckeditor/ckeditor5-horizontal-line';
import {DataSchema, GeneralHtmlSupport} from '@ckeditor/ckeditor5-html-support';
import {Image, ImageInsert, ImageResize, ImageStyle, ImageToolbar, ImageUpload} from '@ckeditor/ckeditor5-image';
import {Indent, IndentBlock} from '@ckeditor/ckeditor5-indent';
import {Link, LinkImage} from '@ckeditor/ckeditor5-link';
import {List, ListProperties} from '@ckeditor/ckeditor5-list';
import {MediaEmbed, MediaEmbedToolbar} from '@ckeditor/ckeditor5-media-embed';
import {PageBreak} from '@ckeditor/ckeditor5-page-break';
import {Paragraph} from '@ckeditor/ckeditor5-paragraph';
import {PasteFromOffice} from '@ckeditor/ckeditor5-paste-from-office';
import {RemoveFormat} from '@ckeditor/ckeditor5-remove-format';
import {
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersMathematical,
	SpecialCharactersText
} from '@ckeditor/ckeditor5-special-characters';
import {Style} from '@ckeditor/ckeditor5-style';
import {Table, TableCellProperties, TableColumnResize, TableProperties, TableToolbar} from '@ckeditor/ckeditor5-table';
import {TextTransformation} from '@ckeditor/ckeditor5-typing';

// You can read more about extending the build with additional plugins in the "Installing plugins" guide.
// See https://ckeditor.com/docs/ckeditor5/latest/installation/plugins/installing-plugins.html for details.

class Editor extends ClassicEditor {
	static builtinPlugins = [
		Alignment,
		Autoformat,
		BlockQuote,
		Bold,
		Code,
		CodeBlock,
		DataSchema,
		Essentials,
		FindAndReplace,
		FontBackgroundColor,
		FontColor,
		FontFamily,
		FontSize,
		GeneralHtmlSupport,
		Heading,
		Highlight,
		HorizontalLine,
		Image,
		ImageInsert,
		ImageResize,
		ImageStyle,
		ImageToolbar,
		ImageUpload,
		Indent,
		IndentBlock,
		Italic,
		Link,
		LinkImage,
		List,
		ListProperties,
		MediaEmbed,
		MediaEmbedToolbar,
		PageBreak,
		Paragraph,
		PasteFromOffice,
		RemoveFormat,
		SpecialCharacters,
		SpecialCharactersArrows,
		SpecialCharactersCurrency,
		SpecialCharactersMathematical,
		SpecialCharactersText,
		SpecialCharactersEssentials,
		Strikethrough,
		Style,
		Subscript,
		Superscript,
		Table,
		TableCellProperties,
		TableColumnResize,
		TableProperties,
		TableToolbar,
		TextTransformation,
		Underline,
		UploadAdapter
	];

	static defaultConfig = {
		toolbar: {
			items: [
				'findAndReplace',
				'heading',
				'|',
				'bold',
				'italic',
				'underline',
				'strikethrough',
				'horizontalLine',
				'alignment',
				'fontFamily',
				'fontSize',
				'fontColor',
				'fontBackgroundColor',
				'|',
				'link',
				'bulletedList',
				// 'numberedList',
				'insertTable',
				'mediaEmbed',
				'specialCharacters',
				'imageUpload',
				'blockQuote',
				'|',
				'removeFormat',
				// 'undo',
				// 'redo',
				// 'specialCharacters',
				// 'outdent',
				// 'indent',
				// 'style',
				// 'subscript',
				// 'superscript',
				// 'findAndReplace',
				// 'imageInsert',
				// 'highlight',
				// 'code',
				// 'codeBlock',
				// 'pageBreak'
			]
		},
		language: 'ru',
		table: {
			contentToolbar: [
				'tableColumn',
				'tableRow',
				'mergeTableCells',
				'tableCellProperties',
				'tableProperties'
			]
		},
		image: {
			resizeUnit: "px",
			toolbar: [
				'imageTextAlternative',
				'imageStyle:alignLeft',
				'imageStyle:alignBlockLeft',
				'imageStyle:alignCenter',
				'imageStyle:alignBlockRight',
				'imageStyle:alignRight',
				'linkImage'
			]
		},
		heading: {
			options: [
				{model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph"},
				{model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1"},
				{model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2"},
				{model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3"},
				{model: "heading4", view: "h4", title: "Heading 4", class: "ck-heading_heading4"},
				{model: "heading5", view: "h5", title: "Heading 5", class: "ck-heading_heading5"},
				{model: "heading6", view: "h6", title: "Heading 6", class: "ck-heading_heading6"}
			]
		},
		mediaEmbed: {
			removeProviders: ['instagram', 'twitter', 'googleMaps', 'flickr', 'facebook'],
			previewsInData: true
		}
	};
}

export default Editor;
